import type { SearchTasksResponse } from '@urbanx/agx.activities.client';
import type { InfiniteData } from '@tanstack/react-query';
import type { TaskUpdatedNotification } from 'types/pushNotifications';
import { applyTaskUpdateToTask } from 'utils/tasks/taskUpdates';
import type { TaskFilterState } from './TaskFilters/TaskFiltersState';

export const taskSearchQueryRoot = ['searchAllTasks'] as const;

export const createTaskSearchQueryKey = (filters?: TaskFilterState) =>
  [...taskSearchQueryRoot, filters] as const;

export type TaskSearchData = InfiniteData<SearchTasksResponse, string | undefined>;

export const restartTaskSearchData = (data: TaskSearchData): TaskSearchData => ({
  pages: [{ ...data.pages[0], continuationToken: undefined }],
  pageParams: [undefined],
});

export const taskUpdateRequiresSearchRestart = (
  data: TaskSearchData,
  taskUpdate: TaskUpdatedNotification
) => {
  if (taskUpdate.collectionStateChanged) return true;

  const loadedTask = data.pages
    .flatMap((page) => page.tasks ?? [])
    .find((task) => task.id === taskUpdate.task.id);

  return (
    loadedTask !== undefined &&
    (loadedTask.dueUtc !== taskUpdate.task.dueUtc ||
      loadedTask.teamType !== taskUpdate.task.teamType ||
      loadedTask.assignedTo !== taskUpdate.task.assignedTo ||
      loadedTask.order !== taskUpdate.task.order)
  );
};

export const removeTaskFromSearchData = (data: TaskSearchData, taskId: string): TaskSearchData => {
  const taskWasLoaded = data.pages.some((page) => page.tasks?.some((task) => task.id === taskId));
  if (!taskWasLoaded) return data;

  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      tasks: page.tasks?.filter((task) => task.id !== taskId),
      totalCount: page.totalCount === undefined ? undefined : page.totalCount - 1,
    })),
  };
};

export const applyTaskUpdateToSearchData = (
  data: TaskSearchData,
  taskUpdate: TaskUpdatedNotification
) => {
  const taskWasLoaded = data.pages.some((page) =>
    page.tasks?.some((task) => task.id === taskUpdate.task.id)
  );
  if (!taskWasLoaded) return data;

  const taskRemainsActive =
    !taskUpdate.task.isLocked && !taskUpdate.task.isCompleted && !!taskUpdate.task.dueUtc;

  if (!taskRemainsActive) {
    return {
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        tasks: page.tasks?.filter((task) => task.id !== taskUpdate.task.id),
        totalCount: page.totalCount === undefined ? undefined : page.totalCount - 1,
      })),
    };
  }

  const pages = data.pages.map((page) => ({
    ...page,
    tasks: page.tasks?.map((task) => {
      if (task.id !== taskUpdate.task.id) return task;

      return applyTaskUpdateToTask(task, taskUpdate);
    }),
  }));

  return { ...data, pages };
};
